/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Building2 } from 'lucide-react';
import { api, ENTITY_TYPES } from '../api';
import { Button, Modal, Input, Label, Select, Badge, useAsync } from '../components/ui';

const JURISDICTIONS = [['IN', 'India'], ['US', 'USA'], ['AE', 'UAE']];

function QuickClientForm({ onSubmit, onCancel }) {
  const [f, setF] = useState({
    companyName: '', pocName: '', email: '', phone: '',
    jurisdictionCode: 'IN', entityType: 'PRIVATE_LIMITED_COMPANY',
  });
  const { busy, err, run } = useAsync();
  return (
    <form onSubmit={(e) => { e.preventDefault(); run(() => onSubmit(f)); }} className="space-y-4">
      <div>
        <Label>Company name</Label>
        <Input required value={f.companyName} onChange={(e) => setF({ ...f, companyName: e.target.value })}
               placeholder="Acme Ventures Pvt Ltd" data-testid="qc-company" />
      </div>
      <div>
        <Label>POC name</Label>
        <Input required value={f.pocName} onChange={(e) => setF({ ...f, pocName: e.target.value })}
               placeholder="e.g. Raj Kumar" data-testid="qc-poc" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Phone</Label>
          <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                 placeholder="+91 …" data-testid="qc-phone" />
        </div>
        <div>
          <Label>Email</Label>
          <Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                 placeholder="poc@company.com" data-testid="qc-email" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Jurisdiction</Label>
          <Select value={f.jurisdictionCode} onChange={(e) => setF({ ...f, jurisdictionCode: e.target.value })}
                  options={JURISDICTIONS} data-testid="qc-jurisdiction" />
        </div>
        <div>
          <Label>Entity type</Label>
          <Select value={f.entityType} onChange={(e) => setF({ ...f, entityType: e.target.value })}
                  options={ENTITY_TYPES} data-testid="qc-entity-type" />
        </div>
      </div>
      {err && <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">{err}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={busy} data-testid="qc-save">{busy ? 'Creating…' : 'Create client'}</Button>
      </div>
    </form>
  );
}

export default function ClientHub() {
  const [clients, setClients] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => api.get('/clients').then((r) => setClients(r.data.items));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="liq-clients-hub">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.32em] text-amber-200/60 mb-2">Clients</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-[32px] leading-tight text-slate-50">Companies<span className="text-amber-300">.</span></h1>
            <p className="text-slate-400 text-[13px] mt-2 max-w-2xl">Each row is a client company with its POC. Open a company to add context, upload KYC / reference documents, and view all its matters.</p>
          </div>
          <button onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 font-semibold text-[13px] px-5 py-2.5 rounded-full hover:brightness-110 hover:scale-[1.02] shadow-lg"
            data-testid="cl-new"><Plus size={14} strokeWidth={2.5} /> New client</button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_180px_140px_120px_100px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/[0.06]">
          <div>Company</div><div>Type</div><div>Jurisdiction</div><div>Docs</div><div>Status</div>
        </div>
        {clients.length === 0 ? (
          <div className="px-5 py-16 text-slate-600 text-sm text-center">
            No clients yet. Click <strong className="text-amber-200">+ New client</strong> to add your first company.
          </div>
        ) : clients.map((c) => (
          <Link key={c.id} to={`/liq/clients/${c.id}`}
            className="grid grid-cols-[1fr_180px_140px_120px_100px] px-5 py-3.5 items-center hover:bg-white/[0.02] border-b border-white/[0.04] last:border-0 group">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Building2 size={16} className="text-emerald-300" />
              </div>
              <div className="min-w-0">
                <div className="text-slate-50 font-medium truncate group-hover:text-amber-100">{c.clientName}</div>
                {c.context && <div className="text-slate-500 text-[11px] truncate">{c.context.slice(0, 90)}</div>}
              </div>
            </div>
            <div className="text-slate-400 text-[12px]">{c.type}</div>
            <div className="text-slate-400 text-[12px]">{c.jurisdictionCode || '—'}</div>
            <div className="text-slate-400 text-[12px]">{(c.documents || []).length}</div>
            <div><Badge tone={c.status === 'Active' ? 'emerald' : 'slate'}>{c.status}</Badge></div>
          </Link>
        ))}
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title="New client">
        <QuickClientForm onCancel={() => setCreating(false)}
          onSubmit={async (f) => { await api.post('/clients-quick', f); setCreating(false); load(); }} />
      </Modal>
    </div>
  );
}
